import {Children} from "../types";
import Icon from "@mdi/react";
import {IconProps} from "@mdi/react/dist/IconProps";
import {HTMLAttributes} from "react";
import styled from "styled-components";
import {darken, lighten, rgba, transitions} from "polished";
import {Accents, getAccentStyle, withAccent} from "../../theme/accents";

export type ButtonProps = {
    onClick?: (e: MouseEvent) => void;
    class?: string;
    title?: string;
} & Children & HTMLAttributes<HTMLButtonElement>;


export const Button = withAccent(styled.button`
    cursor: pointer;
    font: inherit;
    background: ${props => getAccentStyle(props).primaryBg};
    color: ${props => getAccentStyle(props).primaryFg};
    border: none;
    padding: 0.5rem 1rem;
    text-decoration: none;
    
    &[disabled] {
        cursor: default;
        background: ${props => props.theme.colors.mutedBg};
        color: ${props => props.theme.colors.mutedFg};
        
        &:hover {
            background: ${props => props.theme.colors.mutedBg};
        }
    }
    
    &:hover {
        background: ${props => props.theme.colors.hover(getAccentStyle(props).primaryBg)}
    }
    
    &.btn {
        background: transparent;
        color: inherit;
        display: inline-flex;
        align-items: center;
        gap: .2em;
        padding: 0.5rem;
        margin: -0.5rem;
        cursor: pointer;
        
        ${transitions(['color', 'background-color'], '.1s ease-in-out')}
        
        &+.btn {
            margin-left: .5rem;
        }
        
        &[disabled] {
            background: transparent;
        }
        
        &:hover {
            background: ${props => props.theme.colors.hover(getAccentStyle(props).primaryBg)};
            color: ${props => getAccentStyle(props).primaryFg};
        }
    }
`, "primary");

export const IconButton = styled(({ onClick, className, title, ...props}: IconProps & ButtonProps) => {
    return <Button onClick={onClick} className={className} title={title}><Icon size="1em" {...props}></Icon></Button>
})`
`;

export type TitleCallback = (toggled: boolean) => string;

export type ToggleIconProps = {
    initialIcon: string;
    toggledIcon: string;
    toggled?: boolean;
    whenToggled?: (toggled: boolean) => void;
    title?: string | TitleCallback;
} & Omit<IconProps, "path" | "title"> & Omit<ButtonProps, "onClick" | "title">;

export function ToggleIconButton({initialIcon, toggledIcon, whenToggled, title, toggled, ...props}: ToggleIconProps) {
    const toggle = () => {
        whenToggled && whenToggled(!(toggled ?? false));
    }

    const strTitle = (title && typeof(title) === "function")
        ? ((title as ((toggled: boolean) => string)))(toggled ?? false)
        : title;

    return <IconButton
        {...props}
        path={(!(toggled ?? false)) ? initialIcon : toggledIcon}
        onClick={() => { toggle() }}
        title={strTitle}
    />;
}

export const ButtonList = styled.div`
    display: flex;
    height: 100%;
    
    &.text-right {
        justify-content: flex-end;
    }
    
    > *, > *.btn {
        margin: 0;
    }
    
    > .btn + .btn {
        margin-left: 0;
    }
`;
